import { Injectable, Injector } from "@angular/core";
import { Router } from "@angular/router";
import { plainToInstance } from "class-transformer";
import { CookieService } from "ngx-cookie-service";
import { lastValueFrom, Observable, Subscription } from "rxjs";
import { TAPIListResult } from "src/common/contracts/api.contracts";
import { environment } from "src/environments/environment";
import { apiCallWrapper, apiSubscriptionWrapper } from "../../api/api.util";
import { HasIdOrExternalValueOrExternalIdOrServiceName } from "../../api/generic.api";
import { UserImpersonateApi } from "../../api/user/user-impersonate.api";
import { UserImpersonate } from "../../model/user/user-impersonate.model";
import { User } from "../../model/user/user.model";
import { AuthService } from "../auth/auth.service";
import { BaseService } from "../base.service";
import { StorageServiceKey } from "../storage.service";
import { ToastService } from "../toast.service";
import {
  SocketData,
  SocketEventTypeEnum,
  SocketMessagesEnum,
} from "../web-socket/contract/web-socket.contract";
import { WSUserService } from "../web-socket/ws-user.service";
import { UserService } from "./user.service";

@Injectable({
  providedIn: "root",
})
/**
 * @description This service is used to manage user impersonation API.
 */
export class UserImpersonateService extends BaseService {
  impersonationSubscription: Subscription | undefined = undefined;

  constructor(
    private toastService: ToastService,
    private userImpersonateApi: UserImpersonateApi,
    private storageService: CookieService,
    private injector: Injector,
    private router: Router,
  ) {
    super();
  }

  /**
   * @description This method is used to start impersonation of a user.
   * @param currentUserUUID UUID of the current user
   * @param userToImpersonateUUID UUID of the user to impersonate
   * @returns
   */
  public async startImpersonation(
    currentUserUUID: string,
    userToImpersonateUUID: string,
    impersonationReason: string,
  ): Promise<User> {
    try {
      const impersonatedUser = await lastValueFrom(
        apiCallWrapper(
          this.userImpersonateApi.start(
            currentUserUUID,
            userToImpersonateUUID,
            impersonationReason,
          ),
          {
            toastService: this.toastService,
            action: "Starting user impersonation",
          },
        ),
      );

      if (!impersonatedUser) {
        throw new Error();
      }

      const user = plainToInstance(User, impersonatedUser);

      this.storageService.set(
        StorageServiceKey.is_impersonating,
        !!user ? "true" : "false",
        30,
        "/",
        environment.siteDomain,
        true,
        "Strict",
      );

      this._registerWSUserImpersonation();

      return user;
    } catch (error) {
      throw error;
    }
  }

  async stopImpersonation(currentUserUUID: string): Promise<boolean> {
    try {
      const data = await lastValueFrom(
        apiCallWrapper(this.userImpersonateApi.stop(currentUserUUID), {
          toastService: this.toastService,
          action: "Delete Benchmark Filter",
        }),
      );

      if (!data.success) {
        throw new Error();
      }

      this.clearImpersonationFlag();

      return true;
    } catch (error) {
      return false;
    }
  }

  public async getCurrentImpersonationUser(currentUserUUID: string): Promise<User | null> {
    const data = await lastValueFrom(
      apiCallWrapper(this.userImpersonateApi.getCurrentImpersonationUser(currentUserUUID), {
        toastService: this.toastService,
        action: "Get current impersonated user",
      }),
    );

    if (!data) {
      return null;
    }

    return plainToInstance(User, data);
  }

  public clearImpersonationFlag(): void {
    this.storageService.set(
      StorageServiceKey.is_impersonating,
      "false",
      30,
      "/",
      environment.siteDomain,
      true,
      "Strict",
    );
  }

  /**
   * @returns a paginated list of all audit logs
   */
  async getAllLogs(
    skip: number = 0,
    limit: number = 20,
    order: string = "asc",
    sortBy: string = "id",
    search?: string,
    startDate?: string,
    endDate?: string,
  ): Promise<TAPIListResult<UserImpersonate>> {
    try {
      const data = await lastValueFrom(
        apiCallWrapper(
          this.userImpersonateApi.getAllLogs(
            skip,
            limit,
            order,
            sortBy,
            search,
            startDate,
            endDate,
          ),
          {
            toastService: this.toastService,
            action: "Obtaining User Impersonations",
          },
        ),
      );

      return this.inflateModel<
        UserImpersonate,
        UserImpersonate & HasIdOrExternalValueOrExternalIdOrServiceName<string>
      >(UserImpersonate, data);
    } catch (error) {
      throw error;
    }
  }

  public streamAllAuditLogs(
    order: string = "asc",
    sortBy: string = "id",
    search?: string,
    startDate?: string,
    endDate?: string,
  ): Observable<SocketData> {
    const authService = this.injector.get(AuthService);
    return apiSubscriptionWrapper(
      this.userImpersonateApi.streamAllAuditLogs(order, sortBy, search, startDate, endDate),
      {
        authService: authService,
        router: this.router,
        toastService: this.toastService,
        failTitle: "Failed to fetch user impersonation audit data",
      },
    );
  }

  public get isImpersonating(): boolean {
    const isImpersonating = this.storageService.get(StorageServiceKey.is_impersonating);
    return isImpersonating === "true";
  }

  private _registerWSUserImpersonation(): void {
    // Register WebSocket for User Impersonation updates

    this.impersonationSubscription = this.injector.get(WSUserService).getSubscription({
      next: (payload: SocketData) => {
        if (payload.event === SocketEventTypeEnum.Auth) {
          if (
            payload.data.data &&
            payload.data.data.message &&
            payload.data.data.message === SocketMessagesEnum.StopUserImpersonation
          ) {
            const userService = this.injector.get(UserService);
            userService.clearImpersonatedUser();
            this.clearImpersonationFlag();
            this.toastService.showInfo("Impersonation ended. You are now yourself again.");
            this.router.navigateByUrl(userService.defaultLandingPage);
          }
          this.impersonationSubscription?.unsubscribe();
        }
      },
      error: (error) => {
        this.impersonationSubscription?.unsubscribe();
      },
      complete: async () => {
        this.impersonationSubscription?.unsubscribe();
      },
    });
  }
}
